// Tracker — osobní sledovací centrum: sestava, pool, focusy, seznamy, období.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crosshair, MagnifyingGlass, ArrowsClockwise, Binoculars, Stack, ListDashes, Dna, Warning } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { trackerOverview, CANDIDATE_STATUS_LABELS } from "../lib/lab-api";
import { useAuth } from "../contexts/AuthContext";
import { track } from "../lib/analytics";

const PERIODS = [
    { key: "1", label: "1 GW" }, { key: "3", label: "3 GW" },
    { key: "8", label: "8 GW" }, { key: "season", label: "Sezóna" },
];
const MID_KEY = "fa.planner.last_manager_id";

export default function Tracker() {
    const { user, loading: authLoading, login } = useAuth();
    const [period, setPeriod] = useState("3");
    const [managerInput, setManagerInput] = useState(() => localStorage.getItem(MID_KEY) || "");
    const [managerId, setManagerId] = useState(() => localStorage.getItem(MID_KEY) || null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { track("tool_open", { tool: "tracker" }); }, []);

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        trackerOverview({ period, ...(managerId ? { manager_id: managerId } : {}) })
            .then(setData)
            .catch(() => toast.error("Načtení Trackeru selhalo"))
            .finally(() => setLoading(false));
    }, [user, period, managerId]);

    if (!authLoading && !user) {
        return (
            <AppShell>
                <div className="border border-white/10 bg-[#0d0d0d] p-10 text-center space-y-3" data-testid="tracker-login">
                    <p className="font-display font-black uppercase text-xl">Tracker je osobní centrum</p>
                    <p className="font-mono text-[11px] text-white/60">Přihlas se a sleduj svou sestavu, pool i focusy na jednom místě.</p>
                    <button type="button" onClick={login} className="font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black">Přihlásit se</button>
                </div>
            </AppShell>
        );
    }

    const squad = data?.squad;
    const focuses = data?.focuses || [];
    const pool = data?.pool;

    return (
        <AppShell>
            <div className="space-y-5" data-testid="tracker-page">
                <header className="border-2 border-fa-yellow/40 bg-gradient-to-br from-fa-yellow/10 via-transparent to-transparent p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <Crosshair size={11} weight="fill" /> Tracker
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Osobní sledovací centrum</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Co se změnilo u tvého týmu a hráčů, které sleduješ? Sestava, Player Pool, recruitment focusy a seznamy na jednom místě.
                    </p>
                </header>

                <div className="flex flex-wrap items-center gap-2" data-testid="tracker-controls">
                    {PERIODS.map((p) => (
                        <button key={p.key} type="button" onClick={() => setPeriod(p.key)} data-testid={`tracker-period-${p.key}`}
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${period === p.key ? "border-fa-yellow bg-fa-yellow/15 text-fa-yellow" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                            {p.label}
                        </button>
                    ))}
                    {data && <span className="font-mono text-[9px] text-white/40">GW{data.gw_from}–{data.gw_to} (predikce v2)</span>}
                    <div className="flex items-center gap-1.5 ml-auto">
                        <input value={managerInput} onChange={(e) => setManagerInput(e.target.value)} placeholder="FPL Manager ID…"
                               onKeyDown={(e) => { if (e.key === "Enter" && managerInput) { localStorage.setItem(MID_KEY, managerInput); setManagerId(managerInput); } }}
                               className="bg-black/40 border border-white/25 px-3 py-1.5 font-mono text-[11px] text-white w-40 focus:outline-none focus:border-fa-yellow placeholder:text-white/35"
                               data-testid="tracker-manager-input" />
                        <button type="button" onClick={() => { if (managerInput) { localStorage.setItem(MID_KEY, managerInput); setManagerId(managerInput); } }}
                                data-testid="tracker-manager-load"
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow hover:text-black flex items-center gap-1">
                            <MagnifyingGlass size={11} /> Sestava
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="border border-white/10 bg-[#0d0d0d] p-8 font-mono text-[11px] text-white/50 flex items-center gap-2" data-testid="tracker-loading">
                        <ArrowsClockwise size={13} className="animate-spin" /> Načítám tvoje centrum…
                    </div>
                )}

                {!loading && data && (
                    <div className="grid lg:grid-cols-2 gap-4">
                        <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="tracker-squad">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5">
                                Moje sestava {squad && <span className="text-white/45 normal-case">— {squad.team_name} · Σ {squad.total_exp} exp. bodů</span>}
                            </div>
                            {data.squad_error && (
                                <p className="font-mono text-[10px] text-[#FF8800] flex items-center gap-1.5"><Warning size={11} /> {data.squad_error}</p>
                            )}
                            {!squad && !data.squad_error && (
                                <p className="font-mono text-[10px] text-white/45">Zadej FPL Manager ID vpravo nahoře a načti sestavu.</p>
                            )}
                            {squad && (
                                <table className="w-full text-left">
                                    <tbody>
                                        {squad.players.map((p) => (
                                            <tr key={p.fpl_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`tracker-squad-row-${p.fpl_id}`}>
                                                <td className="py-1 pr-2">
                                                    <Link to={`/players/${p.fpl_id}`} className="font-display font-bold text-[12px] hover:text-fa-mint">
                                                        {p.name}{p.is_captain && <span className="text-fa-mint ml-1">(C)</span>}
                                                    </Link>
                                                    {p.news && <span className="font-mono text-[8px] text-[#FF8800] ml-1.5" title={p.news}>⚠</span>}
                                                </td>
                                                <td className="py-1 font-mono text-[9px] text-white/45">{p.team} · {p.position}</td>
                                                <td className="py-1 font-mono text-[10px] text-white/60 tabular-nums text-right">£{p.price?.toFixed(1)}</td>
                                                <td className="py-1 font-mono text-[11px] text-fa-mint font-bold tabular-nums text-right pl-3">{p.exp_period ?? "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </section>

                        <div className="space-y-4">
                            <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="tracker-focuses">
                                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-sky flex items-center gap-1.5">
                                    <Binoculars size={11} /> Aktivní recruitment focusy ({focuses.length})
                                    <Link to="/tools/recruitment-focus" className="ml-auto text-white/40 hover:text-fa-sky normal-case">Otevřít →</Link>
                                </div>
                                {focuses.length === 0 && <p className="font-mono text-[10px] text-white/45">Žádný aktivní focus.</p>}
                                {focuses.map((f) => (
                                    <div key={f.id} className="border border-white/10 p-2.5 space-y-1" data-testid={`tracker-focus-${f.id}`}>
                                        <div className="flex items-center gap-2">
                                            <span className="font-display font-bold text-[13px]">{f.title}</span>
                                            <span className="font-mono text-[8px] uppercase px-1.5 py-0.5 border border-white/20 text-white/50">{f.status}</span>
                                            <span className="font-mono text-[9px] text-white/45 ml-auto tabular-nums">{f.candidates_total}/11</span>
                                        </div>
                                        <div className="font-mono text-[9px] text-white/55">
                                            {f.decision?.main?.name
                                                ? <>Vybrán: <span className="text-fa-mint font-bold">{f.decision.main.name}</span></>
                                                : f.finalists?.length > 0
                                                    ? <>Finalisté: {f.finalists.join(" · ")}</>
                                                    : f.scouting?.length > 0
                                                        ? <>Scouting: {f.scouting.join(", ")}</>
                                                        : "Discover fáze"}
                                            {f.my_tips?.length > 0 && <span className="text-fa-yellow ml-2">★ {f.my_tips.join(", ")}</span>}
                                        </div>
                                    </div>
                                ))}
                            </section>

                            <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="tracker-pool">
                                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5">
                                    <Stack size={11} /> Player Pool ({pool?.total ?? 0})
                                    <Link to="/lab" className="ml-auto text-white/40 hover:text-fa-mint normal-case">Manager Lab →</Link>
                                </div>
                                {(pool?.items || []).length === 0 && <p className="font-mono text-[10px] text-white/45">Pool je prázdný — přidej hráče z profilu nebo finderu.</p>}
                                <div className="flex flex-wrap gap-1.5">
                                    {(pool?.items || []).slice(0, 15).map((d) => (
                                        <Link key={d.fpl_id} to={`/players/${d.fpl_id}`} data-testid={`tracker-pool-${d.fpl_id}`}
                                              className="font-mono text-[10px] px-2 py-1 border border-white/15 hover:border-fa-mint/50 flex items-center gap-1.5">
                                            <span className="font-bold">{d.name || d.display_name}</span>
                                            <span className="text-white/40">{d.status}</span>
                                            {d.exp_period != null && <span className="text-fa-mint tabular-nums">{d.exp_period}</span>}
                                        </Link>
                                    ))}
                                </div>
                            </section>

                            <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="tracker-lists">
                                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-cream flex items-center gap-1.5">
                                    <ListDashes size={11} /> Moje seznamy ({(data.lists || []).length})
                                </div>
                                {(data.lists || []).length === 0 && <p className="font-mono text-[10px] text-white/45">Žádné seznamy — vytvoř je z profilu hráče přes „Přidat…".</p>}
                                <div className="flex flex-wrap gap-1.5">
                                    {(data.lists || []).map((l) => (
                                        <span key={l.id} className="font-mono text-[10px] px-2 py-1 border border-white/15" data-testid={`tracker-list-${l.id}`}>
                                            {l.name} <span className="text-white/40">({l.count})</span>
                                        </span>
                                    ))}
                                </div>
                            </section>

                            <Link to="/tools/season-dna" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border border-fa-yellow/40 text-fa-yellow/80 hover:bg-fa-yellow hover:text-black transition-colors" data-testid="tracker-dna-link">
                                <Dna size={12} /> Team Season DNA nástroj →
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
